"use client";

import { useRef } from "react";
import { motion, useScroll, useTransform } from "motion/react";

/** The home page's hero scroll behaviour, in a reusable shell.
 *
 *  components/sections/Hero.tsx builds this inline for the home page: the
 *  background media creeps down and scales up while the content fades, so the
 *  section greys back into its own dark ground as it leaves the viewport.
 *  The transform values here are that component's, verbatim, so the two
 *  heroes read as one behaviour rather than two near-misses.
 *
 *  Three slots because the layers move differently: `media` takes the dolly,
 *  `overlay` holds the scrims and must stay put (a scrim that scaled with the
 *  photograph would drift off its own gradient stops), and `children` fade.
 *
 *  The `grey` veil is this component's own addition. On the home page the
 *  greying is implicit — content fades and the dark scrims are all that is
 *  left. This hero's photograph sits under a lighter scrim, so it needs an
 *  explicit veil to arrive at the same place.
 */
export function ScrollFadeHero({
  className,
  media,
  overlay,
  children,
}: {
  className?: string;
  media: React.ReactNode;
  overlay?: React.ReactNode;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    // 0 when the section's top meets the top of the viewport, 1 when its
    // bottom does — i.e. progress across the scroll that takes it away.
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.06, 1.14]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);
  const grey = useTransform(scrollYProgress, [0, 0.9], [0, 0.82]);

  return (
    <section ref={ref} className={className}>
      <motion.div style={{ y, scale }} className="pointer-events-none absolute inset-0" aria-hidden>
        {media}
      </motion.div>
      {overlay}
      <motion.div
        style={{ opacity: grey }}
        className="pointer-events-none absolute inset-0 bg-brand-dark"
        aria-hidden
      />
      <motion.div style={{ opacity: fade }} className="relative">
        {children}
      </motion.div>
    </section>
  );
}
